/**
 * @engine/graph — Port control schema
 *
 * A registry-agnostic, declarative description of the inline editor widget a
 * port should show when it is an *unconnected input*. Node definitions already
 * carry the raw hints (`range`, `options`, `variableSelector`, `defaultValue`)
 * on their ports; {@link resolvePortControl} turns those hints + the port's data
 * `type` into a normalized {@link PortControl} the visual editor can render
 * generically — without hard-coding a per-node widget.
 *
 * Lives here (framework-free) so any node system (VFX today, Redprint later) and
 * the Svelte editor in `@engine/graph-editor` share ONE control vocabulary.
 */

import type { BasePortDefinition } from './types';

/** The kind of inline widget a port input shows. */
export type PortControlKind =
  | 'none'      // connector only (flow/event/opaque data) — no inline editor
  | 'number'    // free numeric input
  | 'slider'    // numeric input bounded by min/max
  | 'enum'      // dropdown of `options`
  | 'checkbox'  // boolean toggle
  | 'vector'    // 2/3/4 numeric components
  | 'color'     // color picker (heavy — host popover)
  | 'gradient'  // gradient editor (heavy — host popover)
  | 'curve'     // curve editor (heavy — host popover)
  | 'texture'   // texture/image asset picker (heavy — host)
  | 'pointmap'  // point-map asset picker (heavy — host)
  | 'variable'; // graph-variable selector dropdown

/** Normalized, renderable control descriptor for one port. */
export interface PortControl {
  readonly kind: PortControlKind;
  /** For `slider`. */
  readonly min?: number;
  readonly max?: number;
  /** For `number`/`slider`. */
  readonly step?: number;
  /** For `enum`. */
  readonly options?: readonly string[];
  /** For `vector`: how many components. */
  readonly components?: 2 | 3 | 4;
  /**
   * True when the control cannot be rendered inline by the Svelte island and
   * must be delegated to a host-provided editor (e.g. the Vue HDR color picker,
   * curve/gradient editors, asset pickers).
   */
  readonly heavy?: boolean;
  /** For `texture`/`pointmap`: which asset kind the picker selects. */
  readonly assetKind?: 'image' | 'pointmap';
}

/** Port `type` strings that carry no inline value (execution / opaque wiring). */
const FLOW_TYPES: ReadonlySet<string> = new Set([
  'flow', 'exec', 'event', 'spawnEvent', 'particle',
]);

/** Vector data types → component count. */
const VECTOR_COMPONENTS: Readonly<Record<string, 2 | 3 | 4>> = {
  vec2: 2, vec3: 3, vec4: 4, quat: 4,
};

/** Scalar numeric data types (VFX + Redprint + common spellings). */
const NUMERIC_TYPES: ReadonlySet<string> = new Set([
  'int', 'uint', 'float', 'i32', 'u32', 'f32', 'f64', 'numeric', 'number',
]);

/**
 * Derive the inline control for a port from its declarative hints + data type.
 *
 * Precedence: explicit `port.control` override → flow/opaque → variable selector
 * → enum (`options`) → vector → typed widget (bool/color/gradient/curve/texture/
 * pointmap) → numeric (`slider` if `range`, else `number`) → `none`.
 *
 * Pure and registry-agnostic: unknown types resolve to `none` (connector only).
 */
export function resolvePortControl(port: BasePortDefinition): PortControl {
  if (port.control) return port.control;

  const type = port.type;

  if (port.kind === 'flow' || FLOW_TYPES.has(type)) return { kind: 'none' };
  if (port.variableSelector) return { kind: 'variable' };
  if (port.options && port.options.length > 0) {
    return { kind: 'enum', options: port.options };
  }

  const components = VECTOR_COMPONENTS[type];
  if (components) return { kind: 'vector', components };

  switch (type) {
    case 'bool':      return { kind: 'checkbox' };
    case 'color':     return { kind: 'color', heavy: true };
    case 'gradient':  return { kind: 'gradient', heavy: true };
    case 'curve':     return { kind: 'curve', heavy: true };
    case 'texture2d':
    case 'texture3d': return { kind: 'texture', heavy: true, assetKind: 'image' };
    case 'pointmap':  return { kind: 'pointmap', heavy: true, assetKind: 'pointmap' };
  }

  if (NUMERIC_TYPES.has(type)) {
    return port.range
      ? { kind: 'slider', min: port.range.min, max: port.range.max, step: port.step }
      : { kind: 'number', step: port.step };
  }

  return { kind: 'none' };
}
